import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, FILE_URL } from "@/lib/api";
import { X as XIcon } from "lucide-react";

/**
 * Owner-only modal listing followers or following. Only ever rendered when
 * the viewer IS the profile owner — the calling code guards on `isMe`.
 * Backend also enforces owner-only via 403 so a client-side gap can't leak
 * a follower list.
 *
 * Props:
 *   userId: whose list to load
 *   mode: "followers" | "following"
 *   onClose: dismiss handler
 */
export function FollowListModal({ userId, mode, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/users/${userId}/${mode}`).then(({ data }) => {
      if (alive) setUsers(data.users || []);
    }).catch(() => {
      if (alive) setUsers([]);
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => { alive = false; };
  }, [userId, mode]);

  // Dismiss on Escape — small nicety on desktop.
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const title = mode === "followers" ? "Followers" : "Following";
  const empty = mode === "followers"
    ? "Nobody's followed you yet. When they do, they'll show up here."
    : "You're not following anyone yet. Tap a name in The Grove to follow them.";

  return (
    <div
      data-testid="follow-list-modal"
      className="fixed inset-0 z-50 bg-[#2A2823]/40 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-3xl bg-[#FBF8F1] shadow-[0_20px_60px_rgb(42,40,35,0.15)] border border-[#B79256]/30 my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#B79256]/20">
          <div>
            <div className="accent-label text-[#B79256]">just you can see this</div>
            <h2 className="font-serif-display text-2xl text-[#2A2823] mt-0.5">{title}</h2>
          </div>
          <button
            type="button"
            data-testid="follow-list-close"
            onClick={onClose}
            className="rounded-full p-1.5 text-[#2A2823]/60 hover:text-[#2A2823] hover:bg-[#B79256]/10"
            aria-label="Close"
          >
            <XIcon className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
        <div className="px-2 sm:px-3 py-3 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-[#2A2823]/50">Loading…</p>
          ) : users.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[#2A2823]/60 italic">{empty}</p>
          ) : (
            <ul className="divide-y divide-[#B79256]/15">
              {users.map((u) => {
                const src = u.custom_picture_path ? FILE_URL(u.custom_picture_path) : u.picture;
                return (
                  <li key={u.user_id}>
                    <Link
                      to={`/roots/${u.user_id}`}
                      data-testid={`follow-list-row-${u.user_id}`}
                      onClick={onClose}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-[#B79256]/10 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-[#B79256]/15 flex items-center justify-center shrink-0">
                        {src ? (
                          <img
                            src={src}
                            alt={u.name}
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span className="font-serif-display text-[#4E5A3D]">{u.name?.[0]?.toUpperCase() || "·"}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-serif text-[#2A2823] leading-tight truncate">{u.name}</div>
                        {u.tagline && (
                          <div className="text-xs italic text-[#4E5A3D] truncate">{u.tagline}</div>
                        )}
                        {u.location && (
                          <div className="text-[11px] text-[#2A2823]/50 truncate">{u.location}</div>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
